import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';

interface Staffer {
  id: number;
  userId: number;
  name: string;
  email: string;
  avatarUrl?: string;
  role: string;
  addedOn: string;
  talentScore?: number;
  storyCount: number;
  lastStaffedDate?: string;
  canImpersonate?: boolean;
  canRemove?: boolean;
}

interface BrandProfile {
  id: number;
  name: string;
  accountName: string;
  currentContentStrategyId?: number;
}

interface StandardRole {
  name: string;
  friendlyName: string;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
  a { color: #00a6c2; text-decoration: none; font-size: 14px; &:hover { text-decoration: underline; } }
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const DownloadButton = styled.button`
  padding: 8px 16px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #0094ab; }
`;

const SeatInfo = styled.div`
  margin-bottom: 16px;
  h4 { font-size: 14px; font-weight: 600; margin: 0 0 8px 0; }
  ul { margin: 0; padding-left: 20px; font-size: 14px; color: #374151; }
`;

const StandardRolesLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  font-size: 14px;
  &:hover { text-decoration: underline; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const PlaceholderAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 16px;
`;

const UserInfo = styled.div`
  a { color: #00a6c2; text-decoration: none; font-weight: 500; &:hover { text-decoration: underline; } }
  small { display: block; color: #6b7280; font-size: 12px; }
`;

const ActionButton = styled.button<{ disabled?: boolean }>`
  padding: 6px 10px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  margin-right: 4px;
  opacity: ${({ disabled }) => disabled ? 0.5 : 1};
  filter: drop-shadow(0 0 0.5px rgba(0, 0, 0, 0.3));
  &:hover { 
    background: ${({ disabled }) => disabled ? '#f3f4f6' : '#e5e7eb'}; 
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  color: #111827;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? '#00a6c2' : '#d1d5db')};
  background: ${({ $primary }) => ($primary ? '#00a6c2' : 'white')};
  color: ${({ $primary }) => ($primary ? 'white' : '#374151')};
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

function AdminPublicationUsersPage() {
  const { id } = useParams<{ id: string }>();
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  const [staffers, setStaffers] = useState<Staffer[]>([]);
  const [seatCounts, setSeatCounts] = useState<Record<string, number>>({});
  const [standardRoles, setStandardRoles] = useState<StandardRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingStaffer, setEditingStaffer] = useState<Staffer | null>(null);
  const [editRole, setEditRole] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  // Sorting for users table
  const { sortedData: sortedStaffers, requestSort, getSortDirection } = useSortableData(staffers);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/admin2/publications/${id}/staffer_roles.json`);
      setBrandProfile(res.data.brandProfile);
      setStaffers(res.data.staffers || []);
      setSeatCounts(res.data.seatCounts || {});
      setStandardRoles(res.data.standardRoles || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownloadCSV = () => {
    window.location.href = `/admin2/publications/${id}/staffer_roles.csv`;
  };

  const handleImpersonate = async (userId: number) => {
    try {
      // Follow the server-computed `redirectUrl` (= after_sign_in_path_for,
      // legacy parity → /tasks) instead of hardcoding '/'. See
      // AdminUserEditPage#handleImpersonate for the full rationale.
      const { data } = await api.post(`/admin2/users/${userId}/impersonate`);
      window.location.href = data?.redirectUrl || '/tasks';
    } catch (err) {
      console.error('Failed to impersonate user', err);
      alert('Failed to impersonate user');
    }
  };

  const handleRemoveStaffer = async (stafferId: number, stafferName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${stafferName} from this publication?`)) {
      return;
    }
    try {
      await api.delete(`/admin2/publications/${id}/staffer_roles/${stafferId}.json`);
      // Refresh the data after removal
      fetchData();
    } catch (err) {
      console.error('Failed to remove staffer', err);
      alert('Failed to remove staffer');
    }
  };

  const handleEditRole = (staffer: Staffer) => {
    // Find the matching standard role name (e.g., "Observer" -> "StafferRole::Observer")
    const matchingRole = standardRoles.find((r) => r.friendlyName === staffer.role);
    setEditRole(matchingRole?.name || '');
    setEditingStaffer(staffer);
  };

  const handleSaveRole = async () => {
    if (!editingStaffer || !editRole) return;
    setSavingRole(true);
    try {
      await api.patch(`/admin2/publications/${id}/staffer_roles/${editingStaffer.id}.json`, {
        staffer_role_type: editRole,
      });
      setEditingStaffer(null);
      fetchData();
    } catch (err) {
      console.error('Failed to update role', err);
      alert('Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const pubDisplayName = brandProfile ? `${brandProfile.accountName} - ${brandProfile.name}` : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Publications" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  const totalSeats = Object.values(seatCounts).reduce((a, b) => a + b, 0);

  return (
    <AdminLayout 
      title="Publications" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Users' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{brandProfile?.accountName} - {brandProfile?.name}</Link>
        <span>›</span>
        <span>Users</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Edit {brandProfile?.accountName} - {brandProfile?.name}</PageTitle>
        <HeaderActions>
          <a href={`/admin2/publications/${id}/talent_requests`}>Manage talent requests</a>
          {brandProfile?.currentContentStrategyId && (
            <Link to={`/admin2/content_strategies/${brandProfile.currentContentStrategyId}/edit`}>
              Manage content strategy
            </Link>
          )}
        </HeaderActions>
      </PageHeader>

      <TabNav>
        <TabLink to={`/admin2/publications/${id}/edit`}>Publication info</TabLink>
        <TabLink to={`/admin2/publications/${id}/users`} $active>Users</TabLink>
        <TabLink to={`/admin2/publications/${id}/transactions`}>Transactions</TabLink>
        <TabLink to={`/admin2/publications/${id}/fixed_arr_credits`}>Fixed ARR Credits</TabLink>
        <TabLink to={`/admin2/publications/${id}/bulk_story_tagging`}>Bulk Story Tagging</TabLink>
        <TabLink to={`/admin2/publications/${id}/talent_requests`}>Talent Requests</TabLink>
      </TabNav>

      <Section>
        <SectionHeader>
          <SectionTitle>Users</SectionTitle>
          <DownloadButton onClick={handleDownloadCSV}>Download CSV</DownloadButton>
        </SectionHeader>

        <SeatInfo>
          <h4>Seat count - {totalSeats} total</h4>
          <ul>
            {Object.entries(seatCounts).map(([role, count]) => (
              <li key={role}>{role} - {count}</li>
            ))}
          </ul>
        </SeatInfo>

        <p style={{ marginBottom: 16 }}>
          Map users to new <StandardRolesLink to={`/admin2/publications/${id}/staffer_roles`}>Standard roles</StandardRolesLink>
        </p>

        <Table>
          <thead>
            <tr>
              <Th $sortable $sorted={getSortDirection('name') !== null} onClick={() => requestSort('name')}>
                Name <SortIndicator $direction={getSortDirection('name')} />
              </Th>
              <Th $sortable $sorted={getSortDirection('role') !== null} onClick={() => requestSort('role')}>
                Role <SortIndicator $direction={getSortDirection('role')} />
              </Th>
              <Th $sortable $sorted={getSortDirection('addedOn') !== null} onClick={() => requestSort('addedOn')}>
                Added on <SortIndicator $direction={getSortDirection('addedOn')} />
              </Th>
              <Th $sortable $sorted={getSortDirection('talentScore') !== null} onClick={() => requestSort('talentScore')}>
                Talent Score <SortIndicator $direction={getSortDirection('talentScore')} />
              </Th>
              <Th $sortable $sorted={getSortDirection('storyCount') !== null} onClick={() => requestSort('storyCount')}>
                Story count <SortIndicator $direction={getSortDirection('storyCount')} />
              </Th>
              <Th $sortable $sorted={getSortDirection('lastStaffedDate') !== null} onClick={() => requestSort('lastStaffedDate')}>
                Last Staffed Date <SortIndicator $direction={getSortDirection('lastStaffedDate')} />
              </Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedStaffers.map((staffer) => (
              <tr key={staffer.id}>
                <Td>
                  <UserCell>
                    {staffer.avatarUrl ? (
                      <Avatar src={staffer.avatarUrl} alt={staffer.name} />
                    ) : (
                      <PlaceholderAvatar>👤</PlaceholderAvatar>
                    )}
                    <UserInfo>
                      <Link to={`/admin2/users/${staffer.userId}/edit`}>{staffer.name}</Link>
                      <small>{staffer.email}</small>
                    </UserInfo>
                  </UserCell>
                </Td>
                <Td>{staffer.role}</Td>
                <Td>{staffer.addedOn}</Td>
                <Td>{staffer.talentScore || ''}</Td>
                <Td>{staffer.storyCount}</Td>
                <Td>{staffer.lastStaffedDate || ''}</Td>
                <Td>
                  <ActionButton title="Edit role" onClick={() => handleEditRole(staffer)}>✏️</ActionButton>
                  <ActionButton 
                    title={staffer.canImpersonate ? "Impersonate" : "Cannot impersonate this user"} 
                    onClick={() => staffer.canImpersonate && handleImpersonate(staffer.userId)}
                    disabled={!staffer.canImpersonate}
                  >🕵️</ActionButton>
                  <ActionButton 
                    title={staffer.canRemove ? "Remove staffer" : "Cannot remove this staffer"} 
                    onClick={() => staffer.canRemove && handleRemoveStaffer(staffer.id, staffer.name)}
                    disabled={!staffer.canRemove}
                  >🗑️</ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Section>

      {editingStaffer && (
        <ModalOverlay onClick={() => setEditingStaffer(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit role for {editingStaffer.name}</ModalTitle>
            <ModalLabel>Role</ModalLabel>
            <ModalSelect value={editRole} onChange={(e) => setEditRole(e.target.value)}>
              <option value="">Select a role</option>
              {standardRoles.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.friendlyName}
                </option>
              ))}
            </ModalSelect>
            <ModalFooter>
              <ModalButton onClick={() => setEditingStaffer(null)}>Cancel</ModalButton>
              <ModalButton $primary onClick={handleSaveRole} disabled={!editRole || savingRole}>
                {savingRole ? 'Saving...' : 'Save'}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminLayout>
  );
}

export default AdminPublicationUsersPage;
